import { ref, computed } from 'vue';
import type { MaintenanceInterval } from '@/apiServices/types';

//=================================================================================================
// The seeded dbo.MaintenanceIntervals lookup, cached once per session (PR-129 / Coda child #3).
//
// Frequency options and labels come from data rather than hardcoded arrays. The hardcoded helpers in
// components/shared/frequencyUnits.ts and pm-calendar/helpers/frequencyLabels.ts remain in place as
// the documented fallback ("final tick", Coda child #3 R5) for two cases:
//   * the fetch failed or has not run yet — every accessor degrades to the old behaviour, so a lookup
//     outage can never blank a dropdown or a label;
//   * a (Frequency, UoM) pair has no lookup row — e.g. a legacy cadence nobody seeded.
// Do not delete those helpers here; the article schedules that for a later cleanup once the lookup is
// proven in prod.
//
// Loaded from main.ts's authenticated bootstrap alongside the other session singletons.
//=================================================================================================

const rows = ref<MaintenanceInterval[]>([]);
const loaded = ref(false);

/** Natural key mirroring the DB's unique index and the PR-127 back-fill's UoM normalisation. */
const keyOf = (frequency: number | null | undefined, uom: string | null | undefined): string =>
  `${frequency ?? 0}|${(uom ?? '').trim().toUpperCase()}`;

const byKey = computed(() => {
  const map = new Map<string, MaintenanceInterval>();
  for (const row of rows.value) map.set(keyOf(row.frequency, row.frequencyUoM), row);
  return map;
});

export const MaintenanceIntervalService = {
  /**
   * Fetch the lookup once per session. Never throws: on failure the cache stays empty and every
   * accessor falls back to the hardcoded helper, so a failed load degrades rather than breaks.
   * Logged rather than toasted — this runs during bootstrap, before any page can show a toast.
   */
  async load(): Promise<void> {
    try {
      // Imported lazily so this module — and therefore the pure helpers that read the cache
      // (frequencyUnits.ts, frequencyLabels.ts, formatters.ts) — stays free of the axios instance,
      // which evaluates ConfigService.apiUrl (and thus `window`) at module load. A static import
      // would break every node-environment spec that touches those helpers.
      const { default: configurationAPI } = await import('@/apiServices/configurationAPI');
      const response = await configurationAPI.getMaintenanceIntervalOptions();
      rows.value = response.data ?? [];
      loaded.value = true;
    } catch (error) {
      console.error('MaintenanceIntervalService.load failed; falling back to hardcoded frequencies', error);
      rows.value = [];
      loaded.value = false;
    }
  },

  /** All lookup rows, in DisplayOrder (the endpoint already orders them). */
  rows: computed(() => rows.value),

  /** True once a non-empty lookup has been fetched. Callers use this to pick lookup vs fallback. */
  isLoaded: computed(() => loaded.value && rows.value.length > 0),

  /** The row for a cadence, or undefined when nothing matches. */
  find(frequency: number | null | undefined, uom: string | null | undefined): MaintenanceInterval | undefined {
    return byKey.value.get(keyOf(frequency, uom));
  },

  /** Distinct UoM codes present in the lookup, ordered by the rows' DisplayOrder. */
  uomCodes(): string[] {
    const seen: string[] = [];
    for (const row of rows.value) {
      const code = (row.frequencyUoM ?? '').trim().toUpperCase();
      if (code && !seen.includes(code)) seen.push(code);
    }
    return seen;
  },

  /**
   * Drop the cache. Called on logout alongside the other session singletons so a second user in the
   * same tab re-fetches rather than inheriting the previous session's lookup.
   */
  reset(): void {
    rows.value = [];
    loaded.value = false;
  },
};

export default MaintenanceIntervalService;
